<template>
  <iColumn
    :width="getOptionValue(LayoutWidth) || 'fill'"
    height="hug"
    data-test="message-composer"
    :data-app-field="elementContext?.field || null"
    :style-overrides="storyInputShellStyleOverrides(appInputMargins)"
  >
    <iMessageComposer
      :key="editorVersion"
      ref="composer"
      :on-submit="sendFromKeyboard"
      :upload-file="uploadAttachment"
      :initial-segments="draftSegments"
      :on-user-input="rememberDraft"
      :disabled="editMode"
    >
      <iMessageComposerAttachments label="Attachments to send" remove-label="Remove attachment" />
      <iRow
        width="fill"
        gap="small"
        vertical-align="middle"
        wrap="nowrap"
      >
        <iMessageComposerAttachButton />
        <iMessageComposerInput :placeholder="appInputPlaceholder || 'Write a message'" :style-overrides="composerInputStyleOverrides" />
      </iRow>
    </iMessageComposer>
  </iColumn>
</template>

<script>
import App from "@bloglovin/inspire-engine/App";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import DynamicElementWithContext from "%/dynamic-component-editor/mixins/dynamic-element-with-context-mixin";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import BasicTextOptions from "%/dynamic-component-editor/bundles/basic-text-options";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import DynamicAppInputOptionsMixin from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/dynamic-app-input-options-mixin";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BorderColor from "%/dynamic-component-editor/options/border-color";
import BorderWidth from "%/dynamic-component-editor/options/border-width";
import Placeholder from "%/dynamic-component-editor/options/placeholder";
import PlaceholderColor from "%/dynamic-component-editor/options/placeholder-color";
import CommitAction from "%/dynamic-component-editor/options/commit-action";
import { storyInputShellStyleOverrides } from "%/dynamic-component-editor/helpers/story-editor-style-overrides";

function setStyleOverrides(pairs) {
  return Object.fromEntries(Object.entries(pairs).filter(([, value]) => value !== null && value !== undefined));
}

export default {
  name: "DynamicAppStoryInputElement",
  mixins: [DynamicElementMixin, DynamicElementWithContext, DynamicAppInputOptionsMixin],
  elementPanelSettings: {
    displayName: "Text",
    elementGroup: "general",
    displayIcon: "pencil",
    elementType: "element",
  },
  hidden: true,
  options: [
    BackgroundColor,
    BorderColor,
    BorderWidth,
    Placeholder,
    PlaceholderColor,
    CommitAction,
  ],
  optionBundles: [
    new BasicTextOptions(),
    new LayoutWidthHeightOptions({
      "default.min-height": 100,
    }),
    new SpacingOptions(),
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
  ],
  data() {
    return {
      editorVersion: 0,
      lastWrittenDraft: undefined,
    };
  },
  computed: {
    LayoutWidth() {
      return LayoutWidth;
    },
    appInputPlaceholder() {
      return this.appInputValueIfSet(Placeholder) ?? "";
    },
    composerInputStyleOverrides() {
      const padding = this.appInputPadding;
      return setStyleOverrides({
        backgroundColor: this.appInputBackgroundColor,
        borderColor: this.appInputBorderColor,
        borderWidth: this.appInputBorderWidth,
        focusBorderColor: this.appInputFocusBorderColor,
        borderRadius: this.appInputBorderRadius,
        fontColor: this.appInputFontColor,
        fontFamily: this.appInputFontFamily,
        fontSize: this.appInputFontSize,
        paddingVertical: padding.top ?? padding.bottom ?? null,
        paddingHorizontal: padding.left ?? padding.right ?? null,
        placeholderColor: this.appInputValueIfSet(PlaceholderColor),
      });
    },
    draftSegments() {
      if (this.editMode || typeof this.valueFromContext !== "string" || this.valueFromContext === "") {
        return null;
      }
      const draft = JSON.parse(this.valueFromContext);
      return Array.isArray(draft) ? draft : null;
    },
    sendAction() {
      return this.getOptionValue(CommitAction) || "";
    },
  },
  watch: {
    valueFromContext(next) {
      if (!this.editMode && next !== this.lastWrittenDraft) {
        this.editorVersion += 1;
      }
    },
  },
  methods: {
    storyInputShellStyleOverrides,
    writeDraft(segments) {
      this.lastWrittenDraft = JSON.stringify(segments);
      this.valueFromContext = this.lastWrittenDraft;
    },
    rememberDraft() {
      if (this.editMode) {
        return;
      }
      this.$nextTick(() => {
        const segments = this.$refs.composer?.payload();
        if (segments) {
          this.writeDraft(segments);
        }
      });
    },
    uploadAttachment(file) {
      const app = App.get(this.resolvedAppName);
      if (file.type.startsWith("image/")) {
        return app.uploadStoryImage(file).then(url => ({ url, previewUrl: URL.createObjectURL(file) }));
      }
      return app.uploadStoryFile(file).then(descriptor => ({ url: descriptor.url }));
    },
    sendActionBeside() {
      const row = this.$el?.parentElement;
      const context = this.elementContext?.entityContext ?? "";
      const siblings = row ? [...row.children] : [];
      const button = siblings.find(sibling => sibling !== this.$el && sibling.dataset?.appAction && sibling.dataset.appActionContext === context);
      return button?.dataset.appAction ?? "";
    },
    sendFromKeyboard(segments) {
      this.writeDraft(segments);
      const action = this.sendAction || this.sendActionBeside();
      if (!action) {
        const message = `the ${this.elementContext?.field ?? ""} box has no send action and no app button beside it on the same context, so Enter cannot send; set its send action, or add an app button beside it`;
        // eslint-disable-next-line no-console
        console.error(`DynamicAppStoryInputElement ${this.identifier}: ${message}`, { context: this.elementContext });
        return Promise.reject(new Error(message));
      }
      this.triggerNamedAction(action);
      return Promise.resolve();
    },
  },
};
</script>
